import { useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Trophy } from 'lucide-react'
import { authApi, describeError } from '@/lib/auth'
import { avatarUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import {
  EFFECT_GRADIENT, EFFECT_NEON, NAME_EFFECTS, NAME_FONTS, colorNumber, decorApi, effectSources, entryLine, frameOf, hexColor, lockReason,
  type DecorEntry, type DecorKind,
} from '@/lib/decor'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import type { NameStyle } from '@/types/api'
import { Avatar } from '../Avatar'
import { PlusMark } from '../PlusMark'
import { ServerTag } from '../ServerTag'
import { StyledName } from '../StyledName'
import { DecorPreview, PlateRow } from './DecorPreview'
import { LockedDecorModal } from './LockedDecorModal'
import layer from './SettingsLayer.module.css'
import styles from './DecorationsSection.module.css'

/** Градиент по умолчанию — розовый в сиреневый, как на макете. */
const DEFAULT_COLORS = [0xff7ad9, 0x9d8cff]
/** Цвета профиля до выбора — сиреневый и розовый, как у карточки Дэна на макете. */
const DEFAULT_THEME = [0x9d8cff, 0xff7ad9]

const neonOnly = (s: NameStyle) => s.font_id === 0 && s.effect_id === EFFECT_NEON && s.colors.length === 0
const sameStyle = (a: NameStyle | null, b: NameStyle | null) => JSON.stringify(a) === JSON.stringify(b)

/**
 * «Магазин» — в настройках и отдельной страницей: рамка аватара, эффект профиля и стиль ника. Открытую
 * плитку можно выбрать — справа сразу видно, как выглядит; закрытая по нажатию объясняет, как её получить,
 * и показывает её на вас. page — страница со своим заголовком сверху.
 */
export function DecorationsSection({ page = false }: { page?: boolean }) {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const openSettings = useAppStore((s) => s.openSettings)
  const queryClient = useQueryClient()
  const { data, error: loadError } = useQuery({ queryKey: ['decorations'], queryFn: decorApi.inventory })
  // undefined — не трогали, берём надетое с сервера
  const [frame, setFrame] = useState<string | null | undefined>(undefined)
  const [effect, setEffect] = useState<string | null | undefined>(undefined)
  const [style, setStyle] = useState<NameStyle | null | undefined>(undefined)
  const [theme, setTheme] = useState<number[] | null | undefined>(undefined)
  const [tag, setTag] = useState<string | null | undefined>(undefined)
  const [plate, setPlate] = useState<string | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  // закрытая плитка: окно «как получить» вместо примерки — сохранить её всё равно нельзя
  const [locked, setLocked] = useState<{ kind: DecorKind; entry: DecorEntry } | null>(null)
  if (!user) return null

  const name = user.global_name ?? user.username
  const cur = data?.current
  const frameSel = frame === undefined ? cur?.avatar_decoration ?? null : frame
  const effectSel = effect === undefined ? cur?.profile_effect ?? null : effect
  const styleSel = style === undefined ? cur?.display_name_styles ?? null : style
  const frameChanged = frame !== undefined && frame !== (cur?.avatar_decoration ?? null)
  const effectChanged = effect !== undefined && effect !== (cur?.profile_effect ?? null)
  const styleChanged = style !== undefined && !sameStyle(style, cur?.display_name_styles ?? null)
  const themeSel = theme === undefined ? cur?.theme_colors ?? null : theme
  const themeChanged = theme !== undefined && JSON.stringify(theme) !== JSON.stringify(cur?.theme_colors ?? null)
  const tagSel = tag === undefined ? cur?.server_tag ?? null : tag
  const tagChanged = tag !== undefined && tag !== (cur?.server_tag ?? null)
  const tagOption = data?.server_tags.find((t) => t.guild_id === tagSel)
  const plateSel = plate === undefined ? cur?.nameplate ?? null : plate
  const plateChanged = plate !== undefined && plate !== (cur?.nameplate ?? null)
  const plateEntry = data?.nameplates.find((f) => f.id === plateSel)
  const dirty = frameChanged || effectChanged || styleChanged || themeChanged || tagChanged || plateChanged

  const plus = data?.plus ?? false
  const neonOwned = data?.name_style.neon === 'owned'
  const frameEntry = data?.frames.find((f) => f.id === frameSel)
  const effectEntry = data?.effects.find((f) => f.id === effectSel)
  const blocker =
    frameChanged && frameEntry?.state === 'locked' ? lockReason('frame', frameEntry)
    : effectChanged && effectEntry?.state === 'locked' ? lockReason('effect', effectEntry)
    : plateChanged && plateEntry?.state === 'locked' ? lockReason('plate', plateEntry)
    : styleChanged && styleSel && !plus && !(neonOwned && neonOnly(styleSel))
      ? (neonOnly(styleSel) ? 'Эффект ника «Неон» — за достижение «Диджей» или с Кличат Плюсом' : 'Стиль ника — с Кличат Плюсом')
    : themeChanged && themeSel && !plus ? 'Цвета профиля — с Кличат Плюсом'
      : null

  const reset = () => {
    setFrame(undefined)
    setEffect(undefined)
    setStyle(undefined)
    setTheme(undefined)
    setTag(undefined)
    setPlate(undefined)
    setError(null)
  }
  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const patch: Parameters<typeof authApi.updateProfile>[0] = {}
      if (frameChanged) patch.avatar_decoration = frameSel
      if (effectChanged) patch.profile_effect = effectSel
      if (styleChanged) patch.display_name_styles = styleSel
      if (themeChanged) patch.theme_colors = themeSel
      if (tagChanged) patch.server_tag = tagSel
      if (plateChanged) patch.nameplate = plateSel
      setUser(await authApi.updateProfile(patch))
      reset()
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      await queryClient.invalidateQueries({ queryKey: ['decorations'] })
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  // стиль ника правится по частям: шрифт, эффект, цвета; пустой стиль — обычный ник
  const editStyle = (patch: Partial<NameStyle>) => {
    const base = styleSel ?? { font_id: 0, effect_id: 0, colors: [] }
    const next = { ...base, ...patch }
    if (next.effect_id === EFFECT_GRADIENT && next.colors.length < 2) next.colors = [next.colors[0] ?? DEFAULT_COLORS[0]!, DEFAULT_COLORS[1]!]
    if (next.effect_id !== EFFECT_GRADIENT && next.colors.length > 1) next.colors = next.colors.slice(0, 1)
    setStyle(next.font_id === 0 && next.effect_id === 0 && next.colors.length === 0 ? null : next)
  }
  const colors = styleSel?.colors ?? []
  const avatar = avatarUrl(user.id, user.avatar)
  const initial = name.slice(0, 1).toUpperCase()

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        {page ? null : <h1 className={layer.sectionTitle}>Магазин</h1>}
        <p className={layer.text}>Видны всем: в чате, голосе, списке участников и профиле.</p>
        {loadError ? <p className={styles.error}>{describeError(loadError).message}</p> : null}

        <h2 className={layer.subTitle}>Рамка аватара</h2>
        <div className={styles.frames} role="radiogroup" aria-label="Рамка аватара">
          <Tile selected={frameSel === null} onSelect={() => setFrame(null)} name="Без рамки" line="Всегда">
            <Avatar initial={initial} color="#5865F2" size={44} src={avatar} />
          </Tile>
          {data?.frames.map((f) => (
            <Tile key={f.id} selected={frameSel === f.id} onSelect={() => setFrame(f.id)} onLocked={() => setLocked({ kind: 'frame', entry: f })} name={f.name} entry={f}>
              <Avatar initial={initial} color="#5865F2" size={44} src={avatar} decoration={frameOf(f.id)} />
            </Tile>
          ))}
        </div>

        <h2 className={layer.subTitle}>Эффект профиля</h2>
        <div className={styles.effects} role="radiogroup" aria-label="Эффект профиля">
          <Tile selected={effectSel === null} onSelect={() => setEffect(null)} name="Без эффекта" line="Всегда" wide>
            <span className={styles.effectArt} />
          </Tile>
          {data?.effects.map((f) => {
            const src = effectSources(f.id)
            return (
              <Tile key={f.id} selected={effectSel === f.id} onSelect={() => setEffect(f.id)} onLocked={() => setLocked({ kind: 'effect', entry: f })} name={f.name} entry={f} wide>
                <span className={styles.effectArt}>
                  <img className={styles.effectImage} src={src.src} srcSet={src.srcSet} sizes="160px" alt="" loading="lazy" />
                </span>
              </Tile>
            )
          })}
        </div>

        {data?.nameplates.length ? (
          <>
            <h2 className={layer.subTitle}>Табличка в списке участников</h2>
            <div className={styles.effects} role="radiogroup" aria-label="Табличка">
              <Tile selected={plateSel === null} onSelect={() => setPlate(null)} name="Без таблички" line="Всегда" wide>
                <PlateRow name={name} avatar={avatar} initial={initial} asset={null} />
              </Tile>
              {data.nameplates.map((f) => (
                <Tile key={f.id} selected={plateSel === f.id} onSelect={() => setPlate(f.id)} onLocked={() => setLocked({ kind: 'plate', entry: f })} name={f.name} entry={f} wide>
                  <PlateRow name={name} avatar={avatar} initial={initial} asset={f.id} />
                </Tile>
              ))}
            </div>
          </>
        ) : null}

        <h2 className={cx(layer.subTitle, styles.plusHead)}>Стиль ника <PlusMark size={18} label="С Кличат Плюсом" /></h2>
        <p className={styles.hint}>
          {plus ? 'Одни на все серверы: шрифт, эффект и цвета.' : neonOwned ? 'Эффект «Неон» ваш за «Диджея». Шрифты и свои цвета — с Кличат Плюсом.' : 'С Кличат Плюсом: шрифт, эффект и свои цвета, одни на все серверы.'}
          {!plus ? (
            <button type="button" className={styles.link} onClick={() => openSettings('premium')}>Как получить Плюс</button>
          ) : null}
        </p>
        <div className={styles.chips} role="radiogroup" aria-label="Шрифт ника">
          {NAME_FONTS.map((f) => (
            <button key={f.id} type="button" role="radio" aria-checked={(styleSel?.font_id ?? 0) === f.id} className={cx(styles.chip, (styleSel?.font_id ?? 0) === f.id && styles.chipSelected)} onClick={() => editStyle({ font_id: f.id })}>
              <span className={styles.chipSample}><StyledName name={name} style={{ font_id: f.id, effect_id: 0, colors: [] }} /></span>
              <span className={styles.chipLabel}>{f.label}</span>
            </button>
          ))}
        </div>
        <div className={styles.chips} role="radiogroup" aria-label="Эффект ника">
          {NAME_EFFECTS.map((e) => {
            const sample: NameStyle = { font_id: styleSel?.font_id ?? 0, effect_id: e.id, colors: e.id === EFFECT_GRADIENT ? (colors.length === 2 ? colors : DEFAULT_COLORS) : colors.slice(0, 1) }
            return (
              <button key={e.id} type="button" role="radio" aria-checked={(styleSel?.effect_id ?? 0) === e.id} className={cx(styles.chip, (styleSel?.effect_id ?? 0) === e.id && styles.chipSelected)} onClick={() => editStyle({ effect_id: e.id })}>
                <span className={styles.chipSample}><StyledName name={name} style={sample} /></span>
                <span className={styles.chipLabel}>{e.label}</span>
                {/* «Неон» — единственное из стиля ника, что даётся и без Плюса: за «Диджея» */}
                {e.id === EFFECT_NEON ? <span className={cx(styles.badge, styles.trophy, styles.chipBadge)} data-tooltip="Или за достижение «Диджей»" aria-hidden="true"><Trophy size={11} /></span> : null}
              </button>
            )
          })}
        </div>
        <div className={styles.colors}>
          <label className={styles.color}>
            <input type="color" value={hexColor(colors[0] ?? DEFAULT_COLORS[0]!)} onChange={(e) => editStyle({ colors: [colorNumber(e.target.value), ...colors.slice(1)] })} />
            <span>{colors[0] !== undefined ? hexColor(colors[0]) : 'Свой цвет'}</span>
          </label>
          {styleSel?.effect_id === EFFECT_GRADIENT ? (
            <label className={styles.color}>
              <input type="color" value={hexColor(colors[1] ?? DEFAULT_COLORS[1]!)} onChange={(e) => editStyle({ colors: [colors[0] ?? DEFAULT_COLORS[0]!, colorNumber(e.target.value)] })} />
              <span>{hexColor(colors[1] ?? DEFAULT_COLORS[1]!)}</span>
            </label>
          ) : null}
          {styleSel ? <button type="button" className={styles.link} onClick={() => setStyle(null)}>Обычный ник</button> : null}
        </div>

        <h2 className={cx(layer.subTitle, styles.plusHead)}>Цвета профиля <PlusMark size={18} label="С Кличат Плюсом" /></h2>
        <p className={styles.hint}>{plus ? 'Рамка и фон вашей карточки профиля.' : 'С Кличат Плюсом: рамка и фон вашей карточки профиля.'}</p>
        <div className={styles.colors}>
          {[0, 1].map((i) => {
            const value = themeSel?.[i] ?? DEFAULT_THEME[i]!
            return (
              <label key={i} className={styles.color}>
                <input
                  type="color"
                  value={hexColor(value)}
                  aria-label={i === 0 ? 'Основной цвет' : 'Второй цвет'}
                  onChange={(e) => {
                    const next = [...(themeSel ?? DEFAULT_THEME)]
                    next[i] = colorNumber(e.target.value)
                    setTheme(next)
                  }}
                />
                <span>{themeSel ? hexColor(value) : i === 0 ? 'Основной' : 'Второй'}</span>
              </label>
            )
          })}
          {themeSel ? <button type="button" className={styles.link} onClick={() => setTheme(null)}>Обычные цвета</button> : null}
        </div>

        <h2 className={layer.subTitle}>Тег сервера</h2>
        <p className={styles.hint}>
          {data?.server_tags.length ? 'Рядом с ником в чате, списке участников и профиле.' : 'Тег задаёт сервер в своих настройках. Когда он появится у вашего сервера, его можно будет носить.'}
        </p>
        {data?.server_tags.length ? (
          <div className={styles.chips} role="radiogroup" aria-label="Тег сервера">
            <button type="button" role="radio" aria-checked={tagSel === null} className={cx(styles.chip, tagSel === null && styles.chipSelected)} onClick={() => setTag(null)}>
              <span className={styles.chipSample}>—</span>
              <span className={styles.chipLabel}>Без тега</span>
            </button>
            {data.server_tags.map((t) => (
              <button key={t.guild_id} type="button" role="radio" aria-checked={tagSel === t.guild_id} className={cx(styles.chip, tagSel === t.guild_id && styles.chipSelected)} onClick={() => setTag(t.guild_id)}>
                <span className={styles.chipSample}><ServerTag tag={t.tag} color={t.color} /></span>
                <span className={styles.chipLabel}>{t.guild_name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <aside className={styles.preview} aria-label="Предпросмотр">
        <DecorPreview look={{ frame: frameSel, effect: effectSel, plate: plateSel, style: styleSel, theme: themeSel, tag: tagOption ?? null }} />
        <div className={cx(layer.buttonRow, styles.previewActions)}>
          <button type="button" className={layer.button} disabled={!dirty || busy || Boolean(blocker)} onClick={() => void save()}>
            {saved ? 'Сохранено' : 'Сохранить'}
          </button>
          <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={!dirty || busy} onClick={reset}>Отменить</button>
        </div>
        {blocker ? <p className={styles.blocker}>{blocker}</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}
      </aside>

      {locked ? (
        <LockedDecorModal
          kind={locked.kind}
          entry={locked.entry}
          base={{ frame: frameSel, effect: effectSel, plate: plateSel, style: styleSel, theme: themeSel, tag: tagOption ?? null }}
          onClose={() => setLocked(null)}
        />
      ) : null}

      {/*
        В одну колонку предпросмотр и кнопки оказываются в самом низу, за двумя-тремя экранами плиток:
        выбрал рамку — и не видно ни её, ни «Сохранить». Пока есть что сохранить, внизу экрана висит
        полоса: аватар с выбранной рамкой, ник со стилем и кнопки. На широком экране её нет — там
        предпросмотр едет рядом.
      */}
      {dirty || saved ? (
        <div className={styles.saveBar}>
          <Avatar initial={initial} color="#5865F2" size={40} src={avatar} decoration={frameOf(frameSel)} />
          <span className={styles.saveBarText}>
            {blocker || error ? <span className={error ? styles.error : styles.blocker}>{blocker ?? error}</span> : <StyledName name={name} style={styleSel} className={styles.saveBarName} />}
          </span>
          <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={!dirty || busy} onClick={reset}>Отменить</button>
          <button type="button" className={layer.button} disabled={!dirty || busy || Boolean(blocker)} onClick={() => void save()}>
            {saved ? 'Сохранено' : 'Сохранить'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

interface TileProps {
  selected: boolean
  onSelect: () => void
  /** Нажали на закрытую: объяснить, как получить. */
  onLocked?: () => void
  name: string
  entry?: DecorEntry
  line?: string
  wide?: boolean
  children: ReactNode
}

/**
 * Плитка украшения. Открытую можно выбрать, закрытая помечена замком и подписью, за что даётся, а по нажатию
 * объясняет, как её получить. В углу — откуда предмет: знак Плюса или кубок достижения.
 */
function Tile({ selected, onSelect, onLocked, name, entry, line, wide, children }: TileProps) {
  const locked = entry?.state === 'locked'
  const source = entry?.plus ? 'Кличат Плюс' : entry?.achievement ? `Достижение «${entry.achievement.name}»` : null
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-haspopup={locked && onLocked ? 'dialog' : undefined}
      className={cx(styles.tile, wide && styles.tileWide, selected && styles.tileSelected)}
      onClick={locked && onLocked ? onLocked : onSelect}
    >
      {source ? (
        <span className={cx(styles.badge, styles.tileBadge, !entry?.plus && styles.trophy)} data-tooltip={source} aria-hidden="true">
          {entry?.plus ? <PlusMark size={20} label={null} /> : <Trophy size={12} />}
        </span>
      ) : null}
      <span className={cx(styles.tileArt, locked && styles.tileArtLocked)}>{children}</span>
      <span className={styles.tileName}>{name}</span>
      <span className={cx(styles.tileLine, locked && styles.tileLineLocked)}>
        {locked ? <Lock size={11} aria-hidden="true" /> : null}
        {line ?? (entry ? entryLine(entry) : '')}
      </span>
    </button>
  )
}
